import React, { useEffect, useState } from 'react';
import { Card, Table, Typography, Button, Spin, message, Tag, Image } from 'antd';
import { ArrowLeftOutlined, ExperimentOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { api, TongueCheckinWithUser, tongueImageUrl } from '../utils/api';
import type { ColumnsType } from 'antd/es/table';

const { Title, Text } = Typography;

const TongueManagePage: React.FC = () => {
  const navigate = useNavigate();
  const { authState } = useAuth();
  const [checkins, setCheckins] = useState<TongueCheckinWithUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // 检查用户是否已登录且为医生
  useEffect(() => {
    if (!authState.isLoading && !authState.isAuthenticated) {
      navigate('/login');
    }
    if (!authState.isLoading && authState.isAuthenticated && !authState.user?.is_doctor) {
      message.error('您没有权限访问此页面');
      navigate('/');
    }
  }, [authState.isLoading, authState.isAuthenticated, authState.user, navigate]);

  // 加载打卡列表
  const loadCheckins = async (page: number, size: number) => {
    if (!authState.token) {
      return;
    }

    try {
      setLoading(true);
      const response = await api.getAllTongueList({ page, page_size: size }, authState.token);

      if (response.status === 200 && response.data) {
        setCheckins(response.data.checkins || []);
        setTotal(response.data.total || 0);
        setCurrentPage(response.data.page || page);
        setPageSize(response.data.page_size || size);
      } else {
        message.error(response.msg || '获取舌诊打卡列表失败');
      }
    } catch (error) {
      console.error('获取舌诊打卡列表失败:', error);
      message.error('获取舌诊打卡列表失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (authState.isAuthenticated && authState.token && authState.user?.is_doctor) {
      loadCheckins(currentPage, pageSize);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authState.isAuthenticated, authState.token, authState.user]);

  // 处理分页变化
  const handleTableChange = (pagination: any) => {
    const { current, pageSize: newPageSize } = pagination;
    setCurrentPage(current || 1);
    setPageSize(newPageSize || 10);
    loadCheckins(current || 1, newPageSize || 10);
  };

  // 表格列定义
  const columns: ColumnsType<TongueCheckinWithUser> = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
    },
    {
      title: '用户名',
      dataIndex: 'username',
      key: 'username',
      width: 150,
    },
    {
      title: '舌象照片',
      dataIndex: 'image_url',
      key: 'image_url',
      width: 120,
      render: (imageUrl: string) => (
        <Image
          src={tongueImageUrl(imageUrl)}
          alt="舌象"
          style={{ width: '72px', height: '72px', objectFit: 'cover', borderRadius: '8px' }}
        />
      ),
    },
    {
      title: '识别结果',
      dataIndex: 'labels',
      key: 'labels',
      render: (labels: string[]) =>
        labels && labels.length > 0 ? (
          <>
            {labels.map((label) => (
              <Tag color="volcano" key={label} style={{ marginBottom: '4px' }}>
                {label}
              </Tag>
            ))}
          </>
        ) : (
          <Text type="secondary">未识别到明显特征</Text>
        ),
    },
    {
      title: '打卡时间',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 180,
    },
  ];

  // 如果正在加载
  if (authState.isLoading) {
    return (
      <div style={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center'
      }}>
        <Spin size="large" />
      </div>
    );
  }

  // 如果未登录或不是医生
  if (!authState.isAuthenticated || !authState.user?.is_doctor) {
    return null;
  }

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      padding: '40px 20px',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center'
    }}>
      {/* 顶部导航栏 */}
      <div style={{
        width: '100%',
        maxWidth: '1200px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '40px'
      }}>
        <Button
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate('/')}
          size="large"
          style={{
            background: 'rgba(255, 255, 255, 0.2)',
            border: 'none',
            color: 'white'
          }}
        >
          返回主页
        </Button>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <ExperimentOutlined style={{ fontSize: '32px', color: 'white' }} />
          <Title level={2} style={{ color: 'white', margin: 0 }}>
            舌诊记录管理
          </Title>
        </div>
        <div style={{ width: '100px' }} /> {/* 占位符保持居中 */}
      </div>

      {/* 统计信息 */}
      <div style={{
        width: '100%',
        maxWidth: '1200px',
        marginBottom: '24px'
      }}>
        <Card style={{ borderRadius: '8px', display: 'inline-block' }}>
          <Text strong>总打卡数：</Text>
          <Text style={{ fontSize: '24px', color: '#1890ff', marginLeft: '8px' }}>
            {total}
          </Text>
        </Card>
      </div>

      {/* 打卡列表 */}
      <Card
        style={{
          width: '100%',
          maxWidth: '1200px',
          borderRadius: '16px',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
        }}
      >
        <Table
          columns={columns}
          dataSource={checkins}
          rowKey="id"
          loading={loading}
          pagination={{
            current: currentPage,
            pageSize: pageSize,
            total: total,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total) => `共 ${total} 条`,
            pageSizeOptions: ['10', '20', '50', '100'],
          }}
          onChange={handleTableChange}
          scroll={{ x: 800 }}
        />
      </Card>
    </div>
  );
};

export default TongueManagePage;
